import type { SupportedLocale } from "./locales";

type Section = [string, string];
export type ChatGptCopy = {
  name: string;
  title: string;
  description: string;
  guideTitle: string;
  guideDescription: string;
  eyebrow: string;
  intro: string;
  install: string;
  guide: string;
  settings: string;
  pending: string;
  examples: string;
  copy: string;
  copied: string;
  copyFailed: string;
  demo: string;
  play: string;
  videoNote: string;
  limitsTitle: string;
  limits: string;
  privacy: string;
  privacyLabel: string;
  support: string;
  faqTitle: string;
  manual: string;
  toc: string;
  setup: string;
  benefits: Section[];
  prompts: string[];
  steps: Section[];
  faq: Section[];
};

const copy: Record<SupportedLocale, ChatGptCopy> = {
  en: {
    name: "ChatGPT Transcription",
    title: "Transcribe and explore your videos in ChatGPT",
    description:
      "Connect VideoToTextAI to ChatGPT to transcribe public Instagram and TikTok videos, audio and video attachments, and explore your saved transcripts.",
    guideTitle: "How to transcribe video with ChatGPT and VideoToTextAI",
    guideDescription: "Connect your account, transcribe a Reel or attachment, search saved transcripts, and export subtitles from ChatGPT.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Turn a video into a transcript, then keep the conversation going. Find the main ideas, compare saved Reels, or draft your next piece of content with ChatGPT.",
    install: "Open in ChatGPT",
    guide: "Read the setup guide",
    settings: "Connection settings",
    pending: "Public installation is coming soon. This preview does not provide a public installation link.",
    examples: "Start with a useful question",
    copy: "Copy prompt",
    copied: "Copied",
    copyFailed: "Copy failed. Select and copy the prompt below.",
    demo: "See it in action",
    play: "Play the demonstration",
    videoNote: "The demonstration is in English. Loading the player connects to YouTube.",
    benefits: [
      ["From media to conversation", "Transcribe public Instagram Reels, full TikTok links, or an audio/video file attached in ChatGPT."],
      [
        "Ask your library",
        "Find recent transcripts and explore themes across saved videos. Ask ChatGPT to explain which transcripts it reviewed.",
      ],
      ["Keep working in your app", "Transcripts are saved in VideoToTextAI. Open the project link or export SRT/VTT subtitles."],
    ],
    prompts: [
      "Transcribe this public Instagram Reel and summarize the key points: [paste URL]",
      "What are my latest five Instagram transcripts?",
      "Analyze the lawyer-themed Reels in my saved transcripts. Explain which videos you reviewed.",
      "Transcribe the audio or video file I attached and list the key points.",
      "Export the transcript we just created as SRT.",
    ],
    steps: [
      [
        "1. Open the integration",
        "Open the public VideoToTextAI listing in ChatGPT and follow its installation or connection flow. Availability and labels depend on your ChatGPT account.",
      ],
      [
        "2. Connect your existing account",
        "Sign in to VideoToTextAI on our authorization page. Review permissions for account allowance, transcription jobs, saved transcripts and exports, then choose Connect account. Return to ChatGPT when authorization completes.",
      ],
      [
        "3. Send a supported source",
        "Paste a public Instagram Reel or full TikTok URL, or attach an audio/video file in ChatGPT. Ask VideoToTextAI to transcribe it. ChatGPT supplies a temporary downloadable attachment reference; do not paste /mnt/data paths or attachment IDs manually.",
      ],
      [
        "4. Wait for the transcript",
        "The tool starts a job and checks progress. Ask for the completed result. Avoid submitting the same media repeatedly while a job is pending. The transcript and its project link are saved in your account.",
      ],
      [
        "5. Ask questions and export",
        "Ask for a summary, key quotes, content ideas or subtitle exports. For library analysis, ask which saved transcripts were included and whether more pages remain; a latest-five result is not your entire library.",
      ],
      [
        "6. Manage your connection",
        "Open Connection settings in VideoToTextAI to review usage or disconnect. Revoking access stops further authorized requests; already submitted jobs may finish and saved projects remain. Manage or delete files in your library.",
      ],
    ],
    limitsTitle: "Supported inputs and usage",
    limits:
      "The integration uses your existing account allowance. Public social-link transcription requires an eligible paid account. Attached files can be up to 300 minutes for eligible paid users; free users must submit files under 60 minutes and within their remaining allowance. Files are limited to 1 GiB and require a supported media container with an audio track. Custom media URLs, YouTube pages, private or DRM-protected media, and TikTok short links are unsupported. Share only media you have permission to use.",
    privacy:
      "Only selected tool inputs and resources are sent to VideoToTextAI. Requested transcripts and account/project information are returned to ChatGPT; connecting does not give us your entire conversation. Read the privacy policy before sharing content.",
    privacyLabel: "Privacy policy",
    support: "Contact support",
    faqTitle: "Questions and troubleshooting",
    faq: [
      [
        "Authorization failed",
        "Start a fresh connection from ChatGPT. Sign in to the intended VideoToTextAI account and retry. If the page displays an app error, contact support; a generic Google Translate warning is not proof that a browser extension caused it.",
      ],
      [
        "The attachment was not received",
        "Attach the file again and ask ChatGPT to use VideoToTextAI’s upload tool. A local sandbox path or raw file ID is not a download link. If it still fails, contact support without sharing signed download URLs or authentication tokens.",
      ],
      [
        "The source or allowance is unavailable",
        "Check that the Reel is public, use a full TikTok URL, and verify account access and remaining minutes. Private content and access restrictions cannot be bypassed.",
      ],
      [
        "Can I work without the integration?",
        "Yes. Transcribe supported media in VideoToTextAI, export or copy the transcript, and paste selected text into ChatGPT. Website capabilities and integration capabilities differ.",
      ],
    ],
    manual: "Use VideoToTextAI on the web",
    toc: "On this page",
    setup: "Connect and use the integration",
  },
  es: {
    name: "Transcripción en ChatGPT",
    title: "Transcribe y explora tus vídeos en ChatGPT",
    description:
      "Conecta VideoToTextAI a ChatGPT para transcribir vídeos públicos de Instagram y TikTok, archivos adjuntos y explorar tus transcripciones guardadas.",
    guideTitle: "Cómo transcribir vídeo con ChatGPT y VideoToTextAI",
    guideDescription: "Conecta tu cuenta, transcribe un Reel o archivo, consulta transcripciones y exporta subtítulos desde ChatGPT.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Convierte un vídeo en una transcripción y continúa la conversación. Encuentra ideas principales, compara Reels guardados o prepara tu próximo contenido con ChatGPT.",
    install: "Abrir en ChatGPT",
    guide: "Leer la guía de configuración",
    settings: "Configuración de conexión",
    pending: "La instalación pública estará disponible pronto. Esta vista previa no ofrece un enlace de instalación pública.",
    examples: "Empieza con una pregunta útil",
    copy: "Copiar instrucción",
    copied: "Copiado",
    copyFailed: "No se pudo copiar. Selecciona y copia el texto.",
    demo: "Mira cómo funciona",
    play: "Reproducir demostración",
    videoNote: "La demostración está en inglés. Al cargar el reproductor te conectas a YouTube.",
    benefits: [
      [
        "Del contenido a la conversación",
        "Transcribe Reels públicos de Instagram, enlaces completos de TikTok o archivos de audio/vídeo adjuntos en ChatGPT.",
      ],
      ["Consulta tu biblioteca", "Encuentra transcripciones recientes y analiza temas. Pide a ChatGPT que indique qué vídeos revisó."],
      ["Continúa en tu aplicación", "Las transcripciones se guardan en VideoToTextAI. Abre el proyecto o exporta subtítulos SRT/VTT."],
    ],
    prompts: [
      "Transcribe este Reel público de Instagram y resume sus puntos principales: [URL]",
      "¿Cuáles son mis últimas cinco transcripciones de Instagram?",
      "Analiza los Reels sobre abogados de mis transcripciones guardadas e indica qué vídeos revisaste.",
      "Transcribe el archivo de audio o vídeo adjunto y enumera sus ideas principales.",
      "Exporta la transcripción que acabamos de crear como SRT.",
    ],
    steps: [
      [
        "1. Abre la integración",
        "Abre la ficha pública de VideoToTextAI en ChatGPT y sigue el proceso de instalación o conexión. La disponibilidad y las etiquetas dependen de tu cuenta de ChatGPT.",
      ],
      [
        "2. Conecta tu cuenta existente",
        "Inicia sesión en VideoToTextAI en nuestra página de autorización. Revisa los permisos de uso, trabajos, biblioteca y exportación, y conecta la cuenta. Vuelve a ChatGPT al completar la autorización.",
      ],
      [
        "3. Envía una fuente compatible",
        "Pega un Reel público o una URL completa de TikTok, o adjunta audio/vídeo en ChatGPT. Solicita la transcripción con VideoToTextAI. ChatGPT proporciona un enlace temporal de descarga; no pegues rutas /mnt/data ni identificadores de archivos manualmente.",
      ],
      [
        "4. Espera la transcripción",
        "La herramienta crea un trabajo y consulta su progreso. Pide el resultado completo y evita enviar repetidamente el mismo contenido mientras se procesa. La transcripción y el enlace al proyecto se guardan en tu cuenta.",
      ],
      [
        "5. Pregunta y exporta",
        "Solicita resúmenes, citas, ideas o subtítulos. Para analizar la biblioteca, pregunta qué transcripciones se incluyeron y si quedan más páginas; los últimos cinco resultados no representan toda tu biblioteca.",
      ],
      [
        "6. Gestiona la conexión",
        "Abre la configuración de conexión en VideoToTextAI para revisar uso o desconectar. Revocar bloquea nuevos accesos; los trabajos enviados pueden terminar y los proyectos siguen guardados. Gestiona o elimina archivos en tu biblioteca.",
      ],
    ],
    limitsTitle: "Fuentes compatibles y uso",
    limits:
      "Se utiliza el saldo de tu cuenta. Las URLs sociales requieren una cuenta de pago elegible. Los adjuntos pueden durar hasta 300 minutos para usuarios de pago elegibles; los usuarios gratuitos deben enviar archivos de menos de 60 minutos y dentro de su saldo. Máximo 1 GiB, formato multimedia compatible y pista de audio obligatoria. No se admiten URLs multimedia arbitrarias, páginas de YouTube, contenido privado o con DRM ni enlaces cortos de TikTok. Comparte solo contenido autorizado.",
    privacy:
      "Solo se envían a VideoToTextAI los datos y recursos seleccionados para las herramientas. Los resultados y datos de cuenta/proyecto solicitados vuelven a ChatGPT; la conexión no nos da acceso a toda tu conversación.",
    privacyLabel: "Política de privacidad",
    support: "Contactar con soporte",
    faqTitle: "Preguntas y solución de problemas",
    faq: [
      [
        "Falló la autorización",
        "Inicia una conexión nueva desde ChatGPT y entra en la cuenta correcta. Si aparece un error de la aplicación, contacta con soporte; un aviso genérico de Google Translate no demuestra que una extensión causara el fallo.",
      ],
      [
        "No se recibió el adjunto",
        "Adjunta el archivo otra vez y pide la herramienta de carga de VideoToTextAI. Una ruta local o identificador no es un enlace de descarga. Contacta con soporte sin compartir URLs firmadas ni tokens.",
      ],
      [
        "Fuente o saldo no disponible",
        "Comprueba que el Reel sea público, usa una URL completa de TikTok y revisa acceso y minutos restantes. No se pueden eludir restricciones de acceso.",
      ],
      [
        "¿Puedo trabajar sin la integración?",
        "Sí. Transcribe en VideoToTextAI, copia o exporta el texto y pega fragmentos en ChatGPT. Las funciones del sitio y de la integración son distintas.",
      ],
    ],
    manual: "Usar VideoToTextAI en la web",
    toc: "En esta página",
    setup: "Conectar y usar la integración",
  },
  fr: {
    name: "Transcription avec ChatGPT",
    title: "Transcrivez et explorez vos vidéos dans ChatGPT",
    description:
      "Connectez VideoToTextAI à ChatGPT pour transcrire des vidéos publiques Instagram et TikTok, des pièces jointes et consulter vos transcriptions.",
    guideTitle: "Comment transcrire une vidéo avec ChatGPT et VideoToTextAI",
    guideDescription:
      "Connectez votre compte, transcrivez un Reel ou un fichier, recherchez vos transcriptions et exportez des sous-titres.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Transformez une vidéo en transcription, puis poursuivez la conversation. Retrouvez les idées principales, comparez des Reels ou préparez votre prochain contenu.",
    install: "Ouvrir dans ChatGPT",
    guide: "Lire le guide de connexion",
    settings: "Paramètres de connexion",
    pending: "L’installation publique sera bientôt disponible. Cet aperçu ne propose pas de lien d’installation public.",
    examples: "Commencez par une question utile",
    copy: "Copier la demande",
    copied: "Copié",
    copyFailed: "Échec de copie. Sélectionnez et copiez le texte.",
    demo: "Voir la démonstration",
    play: "Lire la démonstration",
    videoNote: "La démonstration est en anglais. Charger le lecteur vous connecte à YouTube.",
    benefits: [
      [
        "Du média à la conversation",
        "Transcrivez des Reels publics, des liens TikTok complets ou des fichiers audio/vidéo joints dans ChatGPT.",
      ],
      [
        "Interrogez votre bibliothèque",
        "Retrouvez les transcriptions récentes et analysez leurs thèmes. Demandez quels contenus ont été examinés.",
      ],
      [
        "Poursuivez dans votre application",
        "Les transcriptions sont sauvegardées dans VideoToTextAI. Ouvrez le projet ou exportez en SRT/VTT.",
      ],
    ],
    prompts: [
      "Transcris ce Reel Instagram public et résume ses idées principales : [URL]",
      "Quelles sont mes cinq dernières transcriptions Instagram ?",
      "Analyse les Reels sur les avocats dans mes transcriptions et précise les vidéos examinées.",
      "Transcris le fichier audio ou vidéo joint et liste les idées principales.",
      "Exporte en SRT la transcription que nous venons de créer.",
    ],
    steps: [
      [
        "1. Ouvrez l’intégration",
        "Ouvrez la fiche publique VideoToTextAI dans ChatGPT et suivez l’installation ou la connexion. Disponibilité et libellés dépendent de votre compte ChatGPT.",
      ],
      [
        "2. Connectez votre compte existant",
        "Connectez-vous à VideoToTextAI sur notre page d’autorisation. Vérifiez les permissions de consommation, de transcription, de bibliothèque et d’export, puis autorisez la connexion. Revenez à ChatGPT.",
      ],
      [
        "3. Envoyez une source compatible",
        "Collez un Reel public ou une URL TikTok complète, ou joignez un fichier audio/vidéo dans ChatGPT. Demandez une transcription via VideoToTextAI. ChatGPT transmet un lien temporaire de téléchargement ; ne collez pas de chemin /mnt/data ou d’identifiant de fichier.",
      ],
      [
        "4. Attendez la transcription",
        "L’outil lance un travail et vérifie son état. Demandez le résultat complet, sans renvoyer plusieurs fois le même média pendant le traitement. La transcription et le lien du projet sont sauvegardés dans votre compte.",
      ],
      [
        "5. Posez des questions et exportez",
        "Demandez résumés, citations, idées ou sous-titres. Pour une analyse de bibliothèque, demandez quelles transcriptions ont été incluses et s’il reste des pages ; cinq résultats récents ne couvrent pas toute la bibliothèque.",
      ],
      [
        "6. Gérez la connexion",
        "Ouvrez les paramètres de connexion dans VideoToTextAI pour consulter la consommation ou déconnecter. La révocation bloque les prochains accès ; les travaux soumis peuvent terminer et les projets restent sauvegardés. Gérez ou supprimez les fichiers dans votre bibliothèque.",
      ],
    ],
    limitsTitle: "Sources et consommation",
    limits:
      "L’intégration utilise votre quota existant. Les liens sociaux nécessitent un compte payant éligible. Les fichiers joints peuvent durer jusqu’à 300 minutes pour les comptes payants éligibles ; les comptes gratuits doivent rester sous 60 minutes et dans leur quota. Taille maximale : 1 GiB, conteneur compatible avec piste audio. URL arbitraires, pages YouTube, médias privés ou protégés par DRM et liens TikTok courts ne sont pas pris en charge. Partagez uniquement des contenus autorisés.",
    privacy:
      "Seuls les entrées et ressources sélectionnées sont transmises à VideoToTextAI. Les résultats et informations de compte/projet demandés sont renvoyés à ChatGPT ; la connexion ne nous donne pas accès à toute votre conversation.",
    privacyLabel: "Politique de confidentialité",
    support: "Contacter l’assistance",
    faqTitle: "Questions et dépannage",
    faq: [
      [
        "Échec d’autorisation",
        "Démarrez une nouvelle connexion depuis ChatGPT et utilisez le bon compte. Contactez l’assistance en cas d’erreur de l’application ; un avertissement Google Translate générique n’identifie pas la cause.",
      ],
      [
        "Pièce jointe non reçue",
        "Joignez à nouveau le fichier et demandez l’outil de chargement VideoToTextAI. Un chemin local ou identifiant brut n’est pas un lien de téléchargement. Ne partagez pas de liens signés ou de jetons avec l’assistance.",
      ],
      [
        "Source ou quota indisponible",
        "Vérifiez que le Reel est public, utilisez un lien TikTok complet et contrôlez l’accès et les minutes restantes. Les restrictions ne peuvent pas être contournées.",
      ],
      [
        "Puis-je travailler sans l’intégration ?",
        "Oui. Transcrivez dans VideoToTextAI, exportez ou copiez le texte, puis collez des extraits dans ChatGPT. Les fonctions du site et de l’intégration diffèrent.",
      ],
    ],
    manual: "Utiliser VideoToTextAI sur le web",
    toc: "Sur cette page",
    setup: "Connecter et utiliser l’intégration",
  },
  de: {
    name: "Transkription mit ChatGPT",
    title: "Videos in ChatGPT transkribieren und erkunden",
    description:
      "Verbinden Sie VideoToTextAI mit ChatGPT, um öffentliche Instagram-/TikTok-Videos und Anhänge zu transkribieren und gespeicherte Texte zu erkunden.",
    guideTitle: "Videos mit ChatGPT und VideoToTextAI transkribieren",
    guideDescription:
      "Konto verbinden, Reels oder Dateien transkribieren, gespeicherte Texte durchsuchen und Untertitel aus ChatGPT exportieren.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Machen Sie aus einem Video ein Transkript und führen Sie die Unterhaltung fort. Finden Sie Kernaussagen, vergleichen Sie Reels oder entwerfen Sie neue Inhalte.",
    install: "In ChatGPT öffnen",
    guide: "Einrichtungsanleitung lesen",
    settings: "Verbindungseinstellungen",
    pending: "Die öffentliche Installation folgt bald. Diese Vorschau bietet keinen öffentlichen Installationslink.",
    examples: "Mit einer hilfreichen Frage starten",
    copy: "Anfrage kopieren",
    copied: "Kopiert",
    copyFailed: "Kopieren fehlgeschlagen. Text auswählen und kopieren.",
    demo: "So funktioniert es",
    play: "Demonstration abspielen",
    videoNote: "Die Demonstration ist auf Englisch. Beim Laden wird eine Verbindung zu YouTube hergestellt.",
    benefits: [
      ["Vom Medium zum Gespräch", "Transkribieren Sie öffentliche Reels, vollständige TikTok-Links oder Audio-/Videoanhänge in ChatGPT."],
      [
        "Ihre Bibliothek befragen",
        "Finden Sie neue Transkripte und untersuchen Sie Themen. Fragen Sie nach den tatsächlich geprüften Videos.",
      ],
      ["In Ihrer App weiterarbeiten", "Transkripte werden in VideoToTextAI gespeichert. Öffnen Sie Projekte oder exportieren Sie SRT/VTT."],
    ],
    prompts: [
      "Transkribiere dieses öffentliche Instagram-Reel und fasse die Kernaussagen zusammen: [URL]",
      "Was sind meine fünf neuesten Instagram-Transkripte?",
      "Analysiere Reels zum Thema Anwälte in meinen Transkripten und nenne die geprüften Videos.",
      "Transkribiere die angehängte Audio- oder Videodatei und liste die Kernaussagen auf.",
      "Exportiere das gerade erstellte Transkript als SRT.",
    ],
    steps: [
      [
        "1. Integration öffnen",
        "Öffnen Sie den öffentlichen VideoToTextAI-Eintrag in ChatGPT und folgen Sie der Installation oder Verbindung. Verfügbarkeit und Bezeichnungen hängen vom ChatGPT-Konto ab.",
      ],
      [
        "2. Bestehendes Konto verbinden",
        "Melden Sie sich auf unserer Autorisierungsseite bei VideoToTextAI an. Prüfen Sie Berechtigungen für Verbrauch, Aufträge, Bibliothek und Exporte und verbinden Sie das Konto. Kehren Sie zu ChatGPT zurück.",
      ],
      [
        "3. Unterstützte Quelle senden",
        "Fügen Sie ein öffentliches Reel oder eine vollständige TikTok-URL ein oder hängen Sie Audio/Video in ChatGPT an. Bitten Sie VideoToTextAI um Transkription. ChatGPT liefert einen temporären Downloadlink; geben Sie keine /mnt/data-Pfade oder Datei-IDs manuell ein.",
      ],
      [
        "4. Transkript abwarten",
        "Das Werkzeug startet einen Auftrag und prüft den Fortschritt. Fragen Sie nach dem vollständigen Ergebnis und senden Sie dasselbe Medium nicht wiederholt während der Verarbeitung. Transkript und Projektlink werden gespeichert.",
      ],
      [
        "5. Fragen stellen und exportieren",
        "Bitten Sie um Zusammenfassungen, Zitate, Ideen oder Untertitel. Fragen Sie bei Bibliotheksanalysen nach den berücksichtigten Texten und weiteren Seiten; die neuesten fünf Ergebnisse sind nicht die gesamte Bibliothek.",
      ],
      [
        "6. Verbindung verwalten",
        "Öffnen Sie die Verbindungseinstellungen in VideoToTextAI für Verbrauch und Trennung. Widerruf verhindert weitere Zugriffe; eingereichte Aufträge können fertig werden und Projekte bleiben erhalten. Verwalten oder löschen Sie Dateien in Ihrer Bibliothek.",
      ],
    ],
    limitsTitle: "Quellen und Nutzung",
    limits:
      "Die Integration nutzt Ihr bestehendes Kontingent. Social-Links erfordern ein berechtigtes bezahltes Konto. Anhänge dürfen für berechtigte zahlende Nutzer bis zu 300 Minuten lang sein; kostenlose Konten müssen unter 60 Minuten und im Restkontingent bleiben. Maximal 1 GiB, unterstützter Mediencontainer und Audiospur erforderlich. Beliebige Medien-URLs, YouTube-Seiten, private/DRM-Medien und TikTok-Kurzlinks werden nicht unterstützt. Teilen Sie nur autorisierte Inhalte.",
    privacy:
      "Nur ausgewählte Werkzeugeingaben und Ressourcen werden an VideoToTextAI gesendet. Angeforderte Ergebnisse und Konto-/Projektinformationen gehen an ChatGPT zurück; die Verbindung übermittelt nicht die gesamte Unterhaltung.",
    privacyLabel: "Datenschutzerklärung",
    support: "Support kontaktieren",
    faqTitle: "Fragen und Fehlerbehebung",
    faq: [
      [
        "Autorisierung fehlgeschlagen",
        "Starten Sie eine neue Verbindung in ChatGPT und melden Sie sich beim richtigen Konto an. Bei einem App-Fehler kontaktieren Sie den Support; ein allgemeiner Google-Translate-Hinweis beweist keine Ursache.",
      ],
      [
        "Anhang nicht empfangen",
        "Hängen Sie die Datei erneut an und fragen Sie nach dem VideoToTextAI-Uploadwerkzeug. Ein lokaler Pfad oder eine Datei-ID ist kein Downloadlink. Teilen Sie keine signierten URLs oder Token mit dem Support.",
      ],
      [
        "Quelle oder Kontingent fehlt",
        "Prüfen Sie öffentliche Sichtbarkeit, vollständige TikTok-URL, Berechtigungen und Restminuten. Zugriffsbeschränkungen lassen sich nicht umgehen.",
      ],
      [
        "Geht es ohne Integration?",
        "Ja. Transkribieren Sie in VideoToTextAI, exportieren oder kopieren Sie den Text und fügen Sie Ausschnitte in ChatGPT ein. Website und Integration haben unterschiedliche Funktionen.",
      ],
    ],
    manual: "VideoToTextAI im Web nutzen",
    toc: "Auf dieser Seite",
    setup: "Integration verbinden und nutzen",
  },
  it: {
    name: "Trascrizione in ChatGPT",
    title: "Trascrivi ed esplora i tuoi video in ChatGPT",
    description:
      "Collega VideoToTextAI a ChatGPT per trascrivere video pubblici Instagram e TikTok, allegati e consultare le trascrizioni salvate.",
    guideTitle: "Come trascrivere video con ChatGPT e VideoToTextAI",
    guideDescription: "Collega il tuo account, trascrivi un Reel o file, cerca le trascrizioni ed esporta sottotitoli da ChatGPT.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Trasforma un video in una trascrizione e continua la conversazione. Trova idee principali, confronta Reels o prepara il prossimo contenuto.",
    install: "Apri in ChatGPT",
    guide: "Leggi la guida di configurazione",
    settings: "Impostazioni connessione",
    pending: "L’installazione pubblica sarà disponibile presto. Questa anteprima non offre un link di installazione pubblico.",
    examples: "Inizia con una domanda utile",
    copy: "Copia richiesta",
    copied: "Copiato",
    copyFailed: "Copia non riuscita. Seleziona e copia il testo.",
    demo: "Guarda come funziona",
    play: "Riproduci dimostrazione",
    videoNote: "La dimostrazione è in inglese. Caricare il lettore crea una connessione con YouTube.",
    benefits: [
      ["Dal contenuto alla conversazione", "Trascrivi Reels pubblici, link TikTok completi o allegati audio/video in ChatGPT."],
      ["Consulta la biblioteca", "Trova trascrizioni recenti e analizza temi. Chiedi quali video sono stati esaminati."],
      ["Continua nella tua app", "Le trascrizioni vengono salvate in VideoToTextAI. Apri il progetto o esporta in SRT/VTT."],
    ],
    prompts: [
      "Trascrivi questo Reel Instagram pubblico e riassumi i punti principali: [URL]",
      "Quali sono le mie ultime cinque trascrizioni Instagram?",
      "Analizza i Reels sugli avvocati nelle trascrizioni salvate e indica i video esaminati.",
      "Trascrivi il file audio o video allegato ed elenca i punti principali.",
      "Esporta in SRT la trascrizione appena creata.",
    ],
    steps: [
      [
        "1. Apri l’integrazione",
        "Apri la scheda pubblica VideoToTextAI in ChatGPT e segui installazione o collegamento. Disponibilità ed etichette dipendono dal tuo account ChatGPT.",
      ],
      [
        "2. Collega l’account esistente",
        "Accedi a VideoToTextAI nella nostra pagina di autorizzazione. Controlla i permessi di utilizzo, trascrizione, biblioteca ed esportazione e collega l’account. Torna a ChatGPT.",
      ],
      [
        "3. Invia una fonte supportata",
        "Incolla un Reel pubblico o URL TikTok completo, oppure allega audio/video in ChatGPT. Chiedi la trascrizione con VideoToTextAI. ChatGPT fornisce un link temporaneo di download; non incollare percorsi /mnt/data o ID di file.",
      ],
      [
        "4. Attendi la trascrizione",
        "Lo strumento avvia un lavoro e controlla il progresso. Chiedi il risultato completo senza inviare ripetutamente lo stesso contenuto durante l’elaborazione. Trascrizione e link al progetto vengono salvati.",
      ],
      [
        "5. Fai domande ed esporta",
        "Richiedi riassunti, citazioni, idee o sottotitoli. Per analisi della biblioteca chiedi quali testi sono inclusi e se ci sono altre pagine; gli ultimi cinque risultati non coprono tutta la biblioteca.",
      ],
      [
        "6. Gestisci il collegamento",
        "Apri le impostazioni connessione in VideoToTextAI per uso o disconnessione. La revoca blocca accessi futuri; lavori già inviati possono terminare e i progetti restano. Gestisci o elimina file nella biblioteca.",
      ],
    ],
    limitsTitle: "Fonti e utilizzo",
    limits:
      "Si usa il saldo esistente dell’account. I link social richiedono un account a pagamento idoneo. Allegati fino a 300 minuti per utenti paganti idonei; gli utenti gratuiti devono restare sotto 60 minuti e nel saldo residuo. Massimo 1 GiB, formato supportato e traccia audio necessaria. Non sono supportati URL arbitrari, pagine YouTube, contenuti privati/DRM o link TikTok brevi. Condividi solo contenuti autorizzati.",
    privacy:
      "Solo input e risorse selezionati vengono inviati a VideoToTextAI. I risultati e i dati di account/progetto richiesti tornano a ChatGPT; la connessione non ci trasmette tutta la conversazione.",
    privacyLabel: "Informativa privacy",
    support: "Contatta assistenza",
    faqTitle: "Domande e problemi",
    faq: [
      [
        "Autorizzazione non riuscita",
        "Avvia una nuova connessione da ChatGPT e accedi all’account corretto. Contatta assistenza per errori dell’app; un avviso generico Google Translate non dimostra la causa.",
      ],
      [
        "Allegato non ricevuto",
        "Allega nuovamente il file e richiedi lo strumento di caricamento VideoToTextAI. Un percorso locale o ID non è un link di download. Non condividere link firmati o token con assistenza.",
      ],
      [
        "Fonte o saldo non disponibile",
        "Verifica che il Reel sia pubblico, usa un URL TikTok completo e controlla accesso e minuti. Non si possono aggirare restrizioni.",
      ],
      [
        "Posso lavorare senza integrazione?",
        "Sì. Trascrivi in VideoToTextAI, copia o esporta il testo e incolla frammenti in ChatGPT. Le funzioni del sito e dell’integrazione differiscono.",
      ],
    ],
    manual: "Usa VideoToTextAI sul web",
    toc: "In questa pagina",
    setup: "Collegare e usare l’integrazione",
  },
  pt: {
    name: "Transcrição no ChatGPT",
    title: "Transcreva e explore os seus vídeos no ChatGPT",
    description:
      "Ligue VideoToTextAI ao ChatGPT para transcrever vídeos públicos Instagram e TikTok, anexos e explorar transcrições guardadas.",
    guideTitle: "Como transcrever vídeo com ChatGPT e VideoToTextAI",
    guideDescription: "Ligue a conta, transcreva um Reel ou ficheiro, pesquise transcrições e exporte legendas no ChatGPT.",
    eyebrow: "VideoToTextAI + ChatGPT",
    intro:
      "Transforme um vídeo numa transcrição e continue a conversa. Encontre ideias principais, compare Reels ou prepare o próximo conteúdo.",
    install: "Abrir no ChatGPT",
    guide: "Ler o guia de configuração",
    settings: "Definições da ligação",
    pending: "A instalação pública estará disponível em breve. Esta pré-visualização não oferece uma ligação de instalação pública.",
    examples: "Comece com uma pergunta útil",
    copy: "Copiar pedido",
    copied: "Copiado",
    copyFailed: "Falha ao copiar. Selecione e copie o texto.",
    demo: "Veja como funciona",
    play: "Reproduzir demonstração",
    videoNote: "A demonstração está em inglês. Carregar o leitor estabelece uma ligação ao YouTube.",
    benefits: [
      ["Do conteúdo à conversa", "Transcreva Reels públicos, ligações TikTok completas ou anexos áudio/vídeo no ChatGPT."],
      ["Consulte a biblioteca", "Encontre transcrições recentes e analise temas. Peça a lista de vídeos analisados."],
      ["Continue na aplicação", "As transcrições ficam guardadas no VideoToTextAI. Abra o projeto ou exporte SRT/VTT."],
    ],
    prompts: [
      "Transcreva este Reel público do Instagram e resuma os pontos principais: [URL]",
      "Quais são as minhas últimas cinco transcrições do Instagram?",
      "Analise os Reels sobre advogados nas minhas transcrições e indique os vídeos examinados.",
      "Transcreva o ficheiro áudio ou vídeo anexado e liste os pontos principais.",
      "Exporte em SRT a transcrição que acabámos de criar.",
    ],
    steps: [
      [
        "1. Abra a integração",
        "Abra a ficha pública VideoToTextAI no ChatGPT e siga a instalação ou ligação. Disponibilidade e nomes dependem da conta ChatGPT.",
      ],
      [
        "2. Ligue a conta existente",
        "Inicie sessão no VideoToTextAI na nossa página de autorização. Reveja permissões de utilização, transcrição, biblioteca e exportação e ligue a conta. Regresse ao ChatGPT.",
      ],
      [
        "3. Envie uma fonte suportada",
        "Cole um Reel público ou URL TikTok completo, ou anexe áudio/vídeo no ChatGPT. Peça uma transcrição ao VideoToTextAI. ChatGPT fornece uma ligação temporária de descarga; não cole caminhos /mnt/data ou IDs de ficheiros.",
      ],
      [
        "4. Aguarde a transcrição",
        "A ferramenta cria um trabalho e consulta o progresso. Peça o resultado completo e evite enviar repetidamente o mesmo conteúdo durante o processamento. A transcrição e a ligação do projeto ficam guardadas.",
      ],
      [
        "5. Faça perguntas e exporte",
        "Peça resumos, citações, ideias ou legendas. Para analisar a biblioteca, pergunte quais os textos incluídos e se existem mais páginas; cinco resultados recentes não representam toda a biblioteca.",
      ],
      [
        "6. Gira a ligação",
        "Abra as definições da ligação no VideoToTextAI para consultar utilização ou desligar. Revogar impede novos acessos; trabalhos enviados podem terminar e projetos permanecem. Gira ou elimine ficheiros na biblioteca.",
      ],
    ],
    limitsTitle: "Fontes e utilização",
    limits:
      "A integração usa o saldo existente. Ligações sociais exigem uma conta paga elegível. Anexos até 300 minutos para utilizadores pagos elegíveis; contas gratuitas devem enviar menos de 60 minutos e respeitar o saldo. Máximo 1 GiB, formato suportado e faixa áudio obrigatória. URLs arbitrários, páginas YouTube, conteúdo privado/DRM e ligações TikTok curtas não são suportados. Partilhe apenas conteúdo autorizado.",
    privacy:
      "Apenas dados e recursos selecionados são enviados ao VideoToTextAI. Resultados e dados de conta/projeto pedidos regressam ao ChatGPT; a ligação não nos dá acesso à conversa completa.",
    privacyLabel: "Política de privacidade",
    support: "Contactar suporte",
    faqTitle: "Perguntas e resolução de problemas",
    faq: [
      [
        "Falha na autorização",
        "Inicie uma ligação nova no ChatGPT e entre na conta correta. Contacte suporte se existir erro da aplicação; um aviso genérico Google Translate não identifica a causa.",
      ],
      [
        "Anexo não recebido",
        "Anexe novamente o ficheiro e peça a ferramenta de carregamento VideoToTextAI. Um caminho local ou ID não é uma ligação de descarga. Não partilhe URLs assinados ou tokens com suporte.",
      ],
      [
        "Fonte ou saldo indisponível",
        "Confirme que o Reel é público, use uma ligação TikTok completa e verifique acesso e minutos. Não se podem contornar restrições.",
      ],
      [
        "Posso trabalhar sem a integração?",
        "Sim. Transcreva no VideoToTextAI, copie ou exporte o texto e cole excertos no ChatGPT. As funções do site e da integração são diferentes.",
      ],
    ],
    manual: "Usar VideoToTextAI na web",
    toc: "Nesta página",
    setup: "Ligar e usar a integração",
  },
};

export function getChatGptCopy(locale: SupportedLocale): ChatGptCopy {
  return copy[locale];
}
